import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { PassagePanel } from "./PassagePanel";
import { useSpeech } from "../hooks/useSpeech";
import { X, FileText, FileSearch, Loader2, ExternalLink, ArrowDownToLine, Sparkles, Volume2, Square } from "lucide-react";

function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }

function highlight(text, terms) {
  if (!text) return text;
  const clean = (terms || []).map(t => (t || "").trim()).filter(t => t.length >= 2)
    .sort((a, b) => b.length - a.length);
  if (clean.length === 0) return text;
  const pattern = new RegExp(`(${clean.map(escapeRegExp).join("|")})`, "gi");
  return text.split(pattern).map((part, i) =>
    i % 2 === 1
      ? <mark key={i} className="bg-[var(--aria-accent)]/30 text-[var(--aria-text)] rounded-[2px] px-0.5 font-medium">{part}</mark>
      : <span key={i}>{part}</span>
  );
}

/**
 * DocumentReader — opens the source document for a citation.
 *   • Reader tab: reconstructs the full protocol from stored chunks, auto-jumps
 *     to the cited passage and highlights the exact matched keywords. Works for
 *     every document, including seeded/legacy ones with no original PDF.
 *   • Original PDF tab: streams the stored PDF (blob) into an inline viewer,
 *     opened near the estimated source page. Shown only when a file exists.
 */
export default function DocumentReader({ open, onClose, docId, citation }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("reader");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [selection, setSelection] = useState(null);
  const [passage, setPassage] = useState(null);
  const targetRef = useRef(null);
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const { toggle: speakToggle, stop: speakStop, speaking, activeKey, loadingKey } = useSpeech();

  const targetIdx = citation?.chunk_index ?? citation?.page ?? null;

  const onTextSelect = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (!text || text.length < 8 || !bodyRef.current?.contains(sel.anchorNode)) { setSelection(null); return; }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    const host = bodyRef.current.getBoundingClientRect();
    const p = sel.anchorNode?.parentElement?.closest?.("[data-chunk-index]");
    setSelection({ text: text.slice(0, 2000), x: rect.left - host.left + rect.width / 2, y: rect.top - host.top,
                   chunkIndex: p ? Number(p.dataset.chunkIndex) : null });
  };

  const askAboutSelection = () => {
    if (!selection) return;
    setPassage({ text: selection.text, chunkIndex: selection.chunkIndex });
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const askAboutChunk = (c) => setPassage({ text: c.text.slice(0, 2000), chunkIndex: c.chunk_index });

  useEffect(() => {
    if (!open || !docId) return;
    setLoading(true); setData(null); setMode("reader"); setPassage(null); setSelection(null);
    api.get(`/documents/${docId}/content`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ name: citation?.name || "Document", chunks: [], has_file: false }))
      .finally(() => setLoading(false));
  }, [open, docId]); // eslint-disable-line

  // Auto-scroll to the cited chunk once the reader content is painted.
  useEffect(() => {
    if (mode === "reader" && data && targetRef.current) {
      const t = setTimeout(() => {
        targetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 220);
      return () => clearTimeout(t);
    }
  }, [mode, data]);

  // Lazily fetch the original PDF as an authenticated blob when the tab opens.
  useEffect(() => {
    if (mode !== "pdf" || !data?.has_file || pdfUrl) return;
    let revoked = false;
    api.get(`/documents/${docId}/file`, { responseType: "blob" })
      .then(({ data: blob }) => {
        if (revoked) return;
        setPdfUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { revoked = true; };
  }, [mode, data, docId, pdfUrl]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => { if (!open && pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }, [open]); // eslint-disable-line

  // ESC closes the reader.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { speakStop(); onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, speakStop]);

  if (!open) return null;

  const chunks = data?.chunks || [];
  // Prefer the real PDF page attached to the citation/chunk; fall back to a
  // proportional estimate only when no page metadata exists (legacy docs).
  const targetChunk = chunks.find(c => c.chunk_index === targetIdx);
  const estPage = (() => {
    const realPage = citation?.page ?? targetChunk?.page ?? null;
    if (realPage) return Math.max(1, realPage);
    if (!data?.page_count || chunks.length === 0 || targetIdx == null) return 1;
    return Math.min(data.page_count, Math.max(1, Math.round((targetIdx / chunks.length) * data.page_count) + 1));
  })();

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-8" data-testid="document-reader">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[3px]" onClick={onClose} data-testid="document-reader-backdrop" />
      <div className={`relative w-full ${passage ? "max-w-6xl" : "max-w-4xl"} h-[86vh] bg-[var(--aria-surface)] border border-[var(--aria-border)] rounded-lg shadow-[0_24px_100px_rgba(37,99,255,0.22)] flex flex-col overflow-hidden fade-in transition-[max-width] duration-300`}>
        {/* Header */}
        <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-[var(--aria-border)] shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] font-mono tracking-[0.26em] text-[var(--aria-primary)] uppercase">◆ Source document</div>
            <h3 className="font-display text-xl tracking-tight truncate mt-0.5" title={data?.name}>
              {data?.name || citation?.name || "Document"}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex rounded-md border border-[var(--aria-border)] overflow-hidden">
              <button onClick={() => setMode("reader")} data-testid="reader-tab-reader"
                className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${mode === "reader" ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
                <FileSearch size={13} /> Reader
              </button>
              {data?.has_file && (
                <button onClick={() => setMode("pdf")} data-testid="reader-tab-pdf"
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors border-l border-[var(--aria-border)] ${mode === "pdf" ? "bg-[var(--aria-primary)] text-white" : "text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
                  <FileText size={13} /> Original PDF
                </button>
              )}
            </div>
            <button onClick={onClose} data-testid="document-reader-close"
              className="p-2 rounded-sm border border-transparent hover:border-[var(--aria-border)] hover:bg-[var(--aria-surface-2)] transition-colors">
              <X size={18} />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 flex min-h-0">
        <div ref={scrollRef} className="flex-1 overflow-y-auto relative min-w-0">
          {loading && (
            <div className="h-full flex items-center justify-center text-[var(--aria-text-muted)]">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading source…
            </div>
          )}

          {!loading && mode === "reader" && (
            <div ref={bodyRef} onMouseUp={onTextSelect} onKeyUp={onTextSelect} className="relative max-w-2xl mx-auto px-6 sm:px-10 py-8">
              {selection && (
                <button onClick={askAboutSelection} data-testid="ask-selection-button"
                  className="absolute z-10 -translate-x-1/2 -translate-y-full -mt-2 btn btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 shadow-lg ring-glow-primary"
                  style={{ left: selection.x, top: selection.y }}>
                  <Sparkles size={12} /> Ask ARIA about this
                </button>
              )}
              {!passage && chunks.length > 0 && (
                <div className="mb-5 text-[11px] text-[var(--aria-text-muted)] flex items-center gap-1.5" data-testid="reader-selection-hint">
                  <Sparkles size={11} className="text-[var(--aria-primary)]" /> Highlight any sentence to ask ARIA about it — or hover a paragraph to Ask or Listen.
                </div>
              )}
              {citation?.excerpt && (
                <div className="mb-6 rounded-md border border-[var(--aria-accent)]/40 bg-[var(--aria-accent)]/6 px-4 py-3" data-testid="reader-cited-banner">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--aria-accent)] mb-1 flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><ArrowDownToLine size={11} /> ARIA cited this passage</span>
                    {(citation?.page ?? targetChunk?.page) && (
                      <span className="text-[var(--aria-text-muted)]" data-testid="reader-page-label">page {citation?.page ?? targetChunk?.page}</span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--aria-text)] leading-relaxed line-clamp-3 italic">"{citation.excerpt}"</p>
                </div>
              )}
              {data?.file_error && (
                <div className="mb-5 rounded-md border border-amber-500/40 bg-amber-500/8 px-4 py-2.5 text-xs text-amber-300 flex items-center gap-2" data-testid="reader-file-error">
                  <FileText size={13} /> {data.file_error} — showing the reconstructed text instead.
                </div>
              )}
              {chunks.length === 0 && (
                <p className="text-sm text-[var(--aria-text-muted)] italic">No readable content stored for this document.</p>
              )}
              <div className="space-y-4 leading-[1.85] text-[15px] text-[var(--aria-text)]">
                {chunks.map((c) => {
                  const isTarget = c.chunk_index === targetIdx;
                  return (
                    <div key={c.chunk_index} className="group relative" data-chunk-index={c.chunk_index}>
                      <p ref={isTarget ? targetRef : null}
                         data-testid={isTarget ? "reader-target-passage" : `reader-passage-${c.chunk_index}`}
                         className={isTarget
                           ? "rounded-md border-l-2 border-[var(--aria-primary)] bg-[var(--aria-primary)]/6 px-4 py-3 -mx-4 pulse-cite"
                           : ""}>
                        {isTarget ? highlight(c.text, citation?.matched_keywords) : c.text}
                      </p>
                      <div className={`absolute -right-2 top-0 translate-x-full flex flex-col gap-1 transition-opacity ${speaking && activeKey === `chunk-${c.chunk_index}` ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"}`}>
                        <button onClick={() => askAboutChunk(c)} data-testid={`ask-chunk-${c.chunk_index}`}
                          className="text-[11px] text-[var(--aria-primary)] inline-flex items-center gap-1 px-2 py-1 rounded border border-[var(--aria-border)] bg-[var(--aria-surface)]">
                          <Sparkles size={10} /> Ask
                        </button>
                        <button onClick={() => speakToggle(c.text, `chunk-${c.chunk_index}`)} data-testid={`listen-chunk-${c.chunk_index}`}
                          className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-[var(--aria-surface)] ${speaking && activeKey === `chunk-${c.chunk_index}` ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-[var(--aria-border)] text-[var(--aria-text-muted)]"}`}>
                          {loadingKey === `chunk-${c.chunk_index}` ? <Loader2 size={10} className="animate-spin" /> : speaking && activeKey === `chunk-${c.chunk_index}` ? <Square size={10} /> : <Volume2 size={10} />}
                          {speaking && activeKey === `chunk-${c.chunk_index}` ? "Stop" : "Listen"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && mode === "pdf" && (
            <div className="h-full">
              {pdfUrl ? (
                <iframe title="source-pdf" data-testid="reader-pdf-iframe"
                  src={`${pdfUrl}#page=${estPage}`} className="w-full h-full border-0 bg-white" />
              ) : (
                <div className="h-full flex items-center justify-center text-[var(--aria-text-muted)]">
                  <Loader2 className="animate-spin mr-2" size={18} /> Rendering PDF…
                </div>
              )}
            </div>
          )}
        </div>
        {passage && (
          <PassagePanel documentId={docId} passage={passage.text} chunkIndex={passage.chunkIndex} onClose={() => setPassage(null)} />
        )}
        </div>

        {/* Footer */}
        <footer className="shrink-0 px-5 py-2.5 border-t border-[var(--aria-border)] flex items-center justify-between text-[11px] text-[var(--aria-text-muted)] font-mono">
          <span>{chunks.length} passages{data?.page_count ? ` · ${data.page_count} pages` : ""}</span>
          {mode === "pdf" && pdfUrl && (
            <div className="flex items-center gap-4">
              <a href={pdfUrl} download={data?.name || "document.pdf"}
                className="inline-flex items-center gap-1 text-[var(--aria-primary)] hover:underline uppercase tracking-[0.14em]" data-testid="reader-download">
                download <ArrowDownToLine size={11} />
              </a>
              <a href={pdfUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[var(--aria-primary)] hover:underline uppercase tracking-[0.14em]">
                open in new tab <ExternalLink size={11} />
              </a>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
}
